import React, { useState, useCallback } from "react";
import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/formatting";

interface ReadinessBreakdownProps {
  /** Overall readiness score (0-100) */
  overallScore: number;
  /** Short-Term Health sub-score (0-100) */
  shortTermHealthScore: number;
  /** Emergency Fund sub-score (0-100) */
  emergencyFundScore: number;
  /** Cash Flow sub-score (0-100) */
  cashFlowScore: number;
  /** Lifestyle Coverage sub-score (0-100) */
  lifestyleScore: number;
  /** Current emergency fund value */
  emergencyFundCurrent?: number;
  /** Target emergency fund value */
  emergencyFundTarget?: number;
  /** Monthly cash flow surplus (income - expenses) */
  monthlyCashFlowSurplus?: number;
  /** Currency code */
  currency?: string;
  /** Called when user clicks a CTA — passes the target settings category */
  onNavigateToSettings?: (category: string) => void;
}

type ScoreLevel = "good" | "fair" | "poor";

function getScoreLevel(score: number): ScoreLevel {
  if (score >= 75) return "good";
  if (score >= 50) return "fair";
  return "poor";
}

interface PanelConfig {
  id: string;
  name: string;
  score: number;
  explanation: string;
  ctaText: string;
  settingsTarget: string;
}

export const ReadinessBreakdown: React.FC<ReadinessBreakdownProps> = React.memo(
  ({
    shortTermHealthScore,
    emergencyFundScore,
    cashFlowScore,
    lifestyleScore,
    emergencyFundCurrent,
    emergencyFundTarget,
    monthlyCashFlowSurplus,
    currency = "CAD",
    onNavigateToSettings,
  }) => {
    const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
      new Set()
    );

    const togglePanel = useCallback((panelId: string) => {
      setExpandedPanels((prev) => {
        const next = new Set(prev);
        if (next.has(panelId)) {
          next.delete(panelId);
        } else {
          next.add(panelId);
        }
        return next;
      });
    }, []);

    const panels: PanelConfig[] = [
      {
        id: "short-term-health",
        name: "Short-Term Health",
        score: shortTermHealthScore,
        explanation:
          "Measures your ability to handle immediate expenses and emergency cash needs.",
        ctaText: "Review emergency savings →",
        settingsTarget: "investableAssets",
      },
      {
        id: "emergency-fund",
        name: "Emergency Fund",
        score: emergencyFundScore,
        explanation:
          "Assesses whether your safety net covers at least 6 months of expenses.",
        ctaText: "Build your safety net →",
        settingsTarget: "investableAssets",
      },
      {
        id: "cash-flow",
        name: "Cash Flow",
        score: cashFlowScore,
        explanation:
          "Evaluates your monthly income versus expenses to ensure a healthy surplus.",
        ctaText: "Optimize income & expenses →",
        settingsTarget: "income",
      },
      {
        id: "lifestyle",
        name: "Lifestyle Coverage",
        score: lifestyleScore,
        explanation:
          "Projects whether your retirement income will sustain your desired lifestyle.",
        ctaText: "Adjust retirement lifestyle →",
        settingsTarget: "assumptions",
      },
    ];

    return (
      <div className="readiness-breakdown" id="readiness-breakdown">
        <div className="readiness-breakdown-panels">
          {panels.map((panel) => {
            const level = getScoreLevel(panel.score);
            const isExpanded = expandedPanels.has(panel.id);

            return (
              <div key={panel.id} className="readiness-panel">
                {/* Panel Header */}
                <button
                  type="button"
                  className={cn(
                    "readiness-panel-header",
                    `readiness-panel-header--${level}`
                  )}
                  aria-expanded={isExpanded}
                  aria-controls={`panel-content-${panel.id}`}
                  onClick={() => togglePanel(panel.id)}
                >
                  <div className="readiness-panel-header-info">
                    <span className="readiness-panel-name">{panel.name}</span>
                    <span className="readiness-panel-score">
                      {panel.score}%
                    </span>
                  </div>
                  <div className="readiness-panel-bar-track">
                    <div
                      className={cn(
                        "readiness-panel-bar-fill",
                        `readiness-panel-bar-fill--${level}`
                      )}
                      style={{ width: `${Math.min(panel.score, 100)}%` }}
                    />
                  </div>
                  <span
                    className={cn(
                      "readiness-panel-chevron",
                      isExpanded && "readiness-panel-chevron--open"
                    )}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                </button>

                {/* Panel Content */}
                <div
                  id={`panel-content-${panel.id}`}
                  className={cn(
                    "readiness-panel-content",
                    isExpanded && "readiness-panel-content--expanded"
                  )}
                >
                  <div className="readiness-panel-content-inner">
                    <p className="readiness-panel-explanation">
                      {panel.explanation}
                    </p>

                    {/* Progress toward target (for emergency fund) */}
                    {panel.id === "emergency-fund" &&
                      emergencyFundCurrent !== undefined &&
                      emergencyFundTarget !== undefined && (
                        <div className="readiness-target-bar-container">
                          <div className="readiness-target-bar-values">
                            <span className="readiness-target-bar-current">
                              {formatCurrency(emergencyFundCurrent, currency)}
                            </span>
                            <span className="readiness-target-bar-target">
                              Target:{" "}
                              {formatCurrency(emergencyFundTarget, currency)}
                            </span>
                          </div>
                          <div className="readiness-target-bar-track">
                            <div
                              className={cn(
                                "readiness-target-bar-fill",
                                `readiness-target-bar-fill--${level}`
                              )}
                              style={{
                                width: `${Math.min((emergencyFundCurrent / emergencyFundTarget) * 100, 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                    {/* Cash flow detail */}
                    {panel.id === "cash-flow" &&
                      monthlyCashFlowSurplus !== undefined && (
                        <div className="readiness-cashflow-detail">
                          <span
                            className={cn(
                              "readiness-cashflow-value",
                              monthlyCashFlowSurplus >= 0
                                ? "readiness-cashflow-value--positive"
                                : "readiness-cashflow-value--negative"
                            )}
                          >
                            {monthlyCashFlowSurplus >= 0 ? "+" : ""}
                            {formatCurrency(monthlyCashFlowSurplus, currency)}
                            /month
                          </span>
                        </div>
                      )}

                    {/* CTA */}
                    <button
                      type="button"
                      className="readiness-panel-cta"
                      onClick={() =>
                        onNavigateToSettings?.(panel.settingsTarget)
                      }
                    >
                      {panel.ctaText}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

ReadinessBreakdown.displayName = "ReadinessBreakdown";
